'use client';

import type {
  CouponItemDto,
  FailedCouponResult,
  GetCouponsCountServiceResponse,
  GetCouponsServiceResponse,
} from '@musinsa/services-curator';
import {
  COUPON_ERROR_CODES,
  isApiError,
  isUnauthorizedErrorCode,
  receiveAllCouponsService,
} from '@musinsa/services-curator';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { createLoginUrl } from '../../common/auth/redirectToLogin';
import { useCuratorSnackbar } from '../../common/hooks';
import { useNavigate } from '../../common/hooks/useNavigate';
import { curatorQueries } from '../../query-factory';

interface UseReceiveAllCouponsParams {
  shopTitle: string;
}

const DEFAULT_FAILURE_MESSAGE = '쿠폰 발급에 실패하였습니다. 다시 시도해주세요.';

/** 전체 받기 실패분의 errorCode → UI 상태 매핑 (개별 받기는 재클릭 허용을 위해 적용하지 않음) */
const getStatusForFailedResult = (result: FailedCouponResult): CouponItemDto['status'] | null => {
  if (result.errorCode === COUPON_ERROR_CODES.ALREADY_PUBLISHED) return 'RECEIVED';
  if (result.errorCode === COUPON_ERROR_CODES.LIMIT_EXCEEDED) return 'EXHAUSTED';
  return null;
};

export const useReceiveAllCoupons = ({ shopTitle }: UseReceiveAllCouponsParams) => {
  const queryClient = useQueryClient();
  const snackbar = useCuratorSnackbar();
  const { navigate } = useNavigate();

  const { mutate: receiveAllCoupons, isPending } = useMutation({
    mutationFn: () => receiveAllCouponsService({ shopTitle }),
    onSuccess: (data) => {
      const { successCount, failedCount, successCouponNos, failedResults } = data;

      // 성공분 + 실패분(ALREADY→RECEIVED, LIMIT→EXHAUSTED) 모두 UI 상태 최신화
      const statusUpdates = new Map<number, CouponItemDto['status']>();
      for (const couponNo of successCouponNos) {
        statusUpdates.set(couponNo, 'RECEIVED');
      }
      for (const result of failedResults) {
        const nextStatus = getStatusForFailedResult(result);
        if (nextStatus) statusUpdates.set(result.couponNo, nextStatus);
      }

      if (statusUpdates.size > 0) {
        queryClient.setQueryData<InfiniteData<GetCouponsServiceResponse>>(
          curatorQueries.coupon.list(shopTitle).queryKey,
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => ({
                ...page,
                list: page.list.map((coupon) => {
                  const nextStatus = statusUpdates.get(coupon.couponNo);
                  return nextStatus ? { ...coupon, status: nextStatus } : coupon;
                }),
              })),
            };
          },
        );

        queryClient.setQueryData<GetCouponsCountServiceResponse>(
          curatorQueries.coupon.count(shopTitle).queryKey,
          (old) => {
            if (!old) return old;
            return {
              ...old,
              availableCount: Math.max(0, old.availableCount - statusUpdates.size),
            };
          },
        );
      }

      if (successCount > 0 && failedCount === 0) {
        snackbar.success('쿠폰이 모두 발급되었습니다');
      } else if (successCount > 0) {
        snackbar.success(`${successCount}개 쿠폰 발급이 완료되었습니다.`);
      } else {
        snackbar.error(DEFAULT_FAILURE_MESSAGE);
      }
    },
    onError: (error) => {
      if (!isApiError(error)) {
        snackbar.error(DEFAULT_FAILURE_MESSAGE);
        return;
      }

      const errorCode = error.data?.meta?.errorCode;
      const beMessage = error.data?.meta?.message;

      if (isUnauthorizedErrorCode(errorCode)) {
        navigate(createLoginUrl(window.location.href));
        return;
      }

      // NO_COUPONS_AVAILABLE 는 BE 메시지가 이미 "쿠폰 발급에 실패하였습니다..." 로 내려옴
      snackbar.error(beMessage || DEFAULT_FAILURE_MESSAGE);
    },
  });

  return { receiveAllCoupons, isPending };
};
